import type {
  ApprovalResponse,
  Message,
  PublicRunSnapshot,
  RunArtifact,
  RunArtifactPayload,
  ToolExecutionStatus,
  ToolTerminalOutcome,
} from '../../../contracts/harness.types';
import { compareStreamIds } from '../stream/stream-ids';
import { isCancelMarker } from '../timeline/cancel-markers';
import { presentIdentity } from './identity';
import {
  artifactKey,
  snapshotPositionKey,
  streamMessageKey,
  turnMessageKey,
} from './keys';
import { reconcileAcceptedOptimistic } from './reconcile';
import { assistantMessageSignature } from './signature';
import {
  beginDraft,
  bindKeys,
  commitDraft,
  createOrderCursor,
  ensureToolEntry,
  mintPositionalId,
  resolveEntryId,
  writeEntry,
  type OrderCursor,
  type TranscriptDraft,
} from './store';
import type {
  AssistantTranscriptEntry,
  MarkerTranscriptEntry,
  TranscriptMarker,
  TranscriptStore,
  UserTranscriptEntry,
} from './types';

/**
 * INGESTOR 1 — the fetch. Two adapters behind one door: `artifacts[]` when the
 * snapshot carries it (authority for identity AND its own order — `created_at`
 * is display only, never a sort key), otherwise SEAM #1's signature twin-match,
 * which runs once per fetch and never at render.
 *
 * `messages[]` stays a bounded co-source either way for tool results joined on
 * `tool_call_id` — SEAM #2, which dies once the result itself rides the tool
 * execution artifact; only that artifact's status is read here today.
 *
 * Add-or-upgrade only: a fetch that lacks a key touches nothing.
 */
export const ingestSnapshot = (
  store: TranscriptStore,
  snapshot: PublicRunSnapshot
): TranscriptStore => {
  const draft = beginDraft(store);
  settleCaughtUpStream(draft, snapshot.stream_cursor);
  const cursor = createOrderCursor(draft);
  const claim = createClaimIndex(draft);

  if (Array.isArray(snapshot.artifacts)) {
    ingestArtifacts(draft, cursor, claim, snapshot.artifacts);
  } else {
    ingestMessages(draft, cursor, claim, snapshot.messages);
  }

  ingestToolResults(draft, snapshot.messages);
  ingestApprovals(draft, snapshot.approvals);
  reconcileAcceptedOptimistic(draft);

  return commitDraft(store, draft);
};

/**
 * A cursor at or past `lastStreamId` proves every streamed row is persisted, so
 * nothing is still live. Without this a dropped `message_end` strands a
 * streaming row the claim index refuses, leaving a permanent duplicate.
 */
const settleCaughtUpStream = (
  draft: TranscriptDraft,
  streamCursor: string | null
): void => {
  if (!streamCursor) return;
  if (draft.lastStreamId && compareStreamIds(streamCursor, draft.lastStreamId) < 0)
    return;

  for (const entryId of draft.order) {
    const entry = draft.byId[entryId];
    if (entry?.kind !== 'assistant' || !entry.streaming) continue;
    draft.byId[entryId] = { ...entry, streaming: false };
    draft.changed = true;
  }
  if (draft.activeAssistantId !== null) {
    draft.activeAssistantId = null;
    draft.changed = true;
  }
};

const ingestArtifacts = (
  draft: TranscriptDraft,
  cursor: OrderCursor,
  claim: ClaimIndex,
  artifacts: readonly RunArtifact[]
): void => {
  const userPositions = new Map<string, number>();
  for (const artifact of artifacts) {
    const payload = artifact.payload;
    if (payload?.type === 'tool_execution') {
      ingestToolExecution(draft, artifact);
      continue;
    }
    const kind = artifactRecordKind(payload?.type);
    // An unknown type is a payload shipped after this client.
    if (!kind) continue;
    const marker = kind === 'marker' ? artifactMarker(payload) : undefined;
    if (kind === 'marker' && !marker) continue;
    ingestRecord(draft, cursor, claim, {
      keys: artifactKeys(artifact, kind, userPositions),
      kind,
      message: payload?.message,
      ...(marker && { marker }),
      artifactId: artifact.artifact_id,
      turnId: artifact.parent_turn_id,
      messageIndex: artifact.message_index,
      iteration: artifact.iteration ?? undefined,
      createdAt: artifact.created_at ?? undefined,
    });
  }
};

/** Result still rides the `messages[]` seam. */
const ingestToolExecution = (draft: TranscriptDraft, artifact: RunArtifact): void => {
  const payload = artifact.payload;
  const toolCallId = payload.tool_call_id;
  if (!toolCallId) return;
  const status = toolExecutionStatus(payload.status);
  const terminalOutcome = toolTerminalOutcome(payload.terminal_outcome);
  const resultCreatedAt = artifact.created_at ?? undefined;
  const turnId = artifact.parent_turn_id;
  const iteration = artifact.iteration ?? undefined;
  const entry = ensureToolEntry(draft, toolCallId, 'persisted');
  if (
    entry.source === 'persisted' &&
    entry.persistedStatus === status &&
    entry.terminalOutcome === terminalOutcome &&
    entry.resultCreatedAt === resultCreatedAt &&
    entry.turnId === turnId &&
    entry.iteration === iteration
  ) {
    return;
  }
  writeEntry(draft, {
    ...entry,
    source: 'persisted',
    ...(status !== undefined && { persistedStatus: status }),
    ...(terminalOutcome !== undefined && { terminalOutcome }),
    ...(resultCreatedAt !== undefined && { resultCreatedAt }),
    ...presentIdentity({ turnId, iteration }),
  });
};

const TOOL_EXECUTION_STATUSES: readonly ToolExecutionStatus[] = [
  'completed',
  'failed',
  'denied',
  'timed_out',
  'cancelled',
];

const TOOL_TERMINAL_OUTCOMES: readonly ToolTerminalOutcome[] = [
  'succeeded',
  'blocked',
  'failed',
];

const toolExecutionStatus = (
  value: string | undefined
): ToolExecutionStatus | undefined =>
  TOOL_EXECUTION_STATUSES.find((status) => status === value);

const toolTerminalOutcome = (
  value: string | null | undefined
): ToolTerminalOutcome | undefined =>
  TOOL_TERMINAL_OUTCOMES.find((outcome) => outcome === value);

/**
 * `message_index` is TURN-scoped, so both roles of a turn share one. The role
 * picks the prefix, which also converges each side on the key its live source
 * already binds: `turn:` for the POST echo, `stream:` for `message_start`.
 */
const artifactKeys = (
  artifact: RunArtifact,
  kind: SnapshotRecordKind,
  userPositions: Map<string, number>
): string[] => {
  const keys = [artifactKey(artifact.artifact_id)];
  if (kind === 'marker' || !artifact.parent_turn_id) return keys;
  if (kind === 'user') {
    // The backend persists each user message of a turn as its own artifact,
    // each at `message_index` 0. Key by position within the turn instead (the
    // optimistic ingestor's position) so a routine seed and the clinician's
    // message that follows it stay two entries rather than one overwriting
    // the other.
    const position = userPositions.get(artifact.parent_turn_id) ?? 0;
    userPositions.set(artifact.parent_turn_id, position + 1);
    keys.push(turnMessageKey(artifact.parent_turn_id, position));
  } else if (artifact.iteration !== null && artifact.iteration !== undefined) {
    keys.push(
      streamMessageKey(
        artifact.parent_turn_id,
        artifact.iteration,
        artifact.message_index
      )
    );
  }
  return keys;
};

const ingestMessages = (
  draft: TranscriptDraft,
  cursor: OrderCursor,
  claim: ClaimIndex,
  messages: readonly Message[]
): void => {
  messages.forEach((message, index) => {
    ingestRecord(draft, cursor, claim, {
      keys: [snapshotPositionKey(index)],
      ...messageRecordKind(message),
      message,
      messageIndex: index,
    });
  });
};

type SnapshotRecordKind = 'user' | 'assistant' | 'marker';

interface SnapshotRecord {
  /** Most specific first: the first key that resolves wins. */
  keys: string[];
  kind: SnapshotRecordKind;
  message: Message | undefined;
  marker?: TranscriptMarker;
  artifactId?: string;
  turnId?: string;
  messageIndex?: number;
  iteration?: number;
  createdAt?: string;
}

const ingestRecord = (
  draft: TranscriptDraft,
  cursor: OrderCursor,
  claim: ClaimIndex,
  record: SnapshotRecord
): void => {
  const existingId = resolveEntryId(draft, record.keys);
  if (existingId) {
    // A misjoin degrades to a no-op rather than overwriting the other role.
    if (draft.byId[existingId]?.kind !== record.kind) return;
    cursor.visit(existingId);
    upgradeExisting(draft, existingId, record);
    bindKeys(draft, existingId, record.keys);
    return;
  }

  if (record.kind === 'marker') {
    if (!record.marker) return;
    const entry: MarkerTranscriptEntry = {
      id: mintPositionalId(draft),
      kind: 'marker',
      source: 'persisted',
      ...record.marker,
      ...(record.artifactId !== undefined && { artifactId: record.artifactId }),
      ...presentIdentity({
        turnId: record.turnId,
        iteration: record.iteration,
        createdAt: record.createdAt,
      }),
    };
    cursor.insert(entry);
    bindKeys(draft, entry.id, record.keys);
    return;
  }

  const message = record.message;
  if (!message) return;

  if (record.kind === 'user') {
    const entry: UserTranscriptEntry = {
      id: mintPositionalId(draft),
      kind: 'user',
      source: 'persisted',
      message,
      position: 0,
      accepted: true,
      ...presentIdentity({
        turnId: record.turnId,
        iteration: record.iteration,
        createdAt: record.createdAt,
      }),
    };
    cursor.insert(entry);
    bindKeys(draft, entry.id, record.keys);
    return;
  }

  const twinId = claim.claimAssistant(assistantMessageSignature(message));
  if (twinId) {
    cursor.visit(twinId);
    upgradeExisting(draft, twinId, record);
    bindKeys(draft, twinId, record.keys);
    return;
  }

  const entry: AssistantTranscriptEntry = {
    id: mintPositionalId(draft),
    kind: 'assistant',
    source: 'persisted',
    message,
    streaming: false,
    ...(record.artifactId !== undefined && { artifactId: record.artifactId }),
    ...(record.messageIndex !== undefined && { messageIndex: record.messageIndex }),
    ...presentIdentity({
      turnId: record.turnId,
      iteration: record.iteration,
      createdAt: record.createdAt,
    }),
  };
  cursor.insert(entry);
  bindKeys(draft, entry.id, record.keys);
};

const upgradeExisting = (
  draft: TranscriptDraft,
  entryId: string,
  record: SnapshotRecord
): void => {
  const entry = draft.byId[entryId];
  if (!entry || !record.message) return;
  if (entry.kind === 'user') {
    // React Query structural sharing returns the same object on a deep-equal
    // poll, so reference equality keeps a steady poll from re-minting the store.
    if (
      entry.source === 'persisted' &&
      entry.accepted &&
      entry.message === record.message &&
      upgradeIdentityUnchanged(entry, record)
    ) {
      return;
    }
    writeEntry(draft, {
      ...entry,
      source: 'persisted',
      accepted: true,
      message: record.message,
      ...presentIdentity({
        turnId: record.turnId,
        iteration: record.iteration,
        createdAt: record.createdAt,
      }),
    });
    return;
  }
  if (entry.kind === 'assistant') {
    if (
      entry.source === 'persisted' &&
      !entry.streaming &&
      entry.message === record.message &&
      upgradeIdentityUnchanged(entry, record)
    ) {
      return;
    }
    writeEntry(draft, {
      ...entry,
      source: 'persisted',
      streaming: false,
      message: record.message,
      ...(record.artifactId !== undefined && { artifactId: record.artifactId }),
      ...(record.messageIndex !== undefined && { messageIndex: record.messageIndex }),
      ...presentIdentity({
        turnId: record.turnId,
        iteration: record.iteration,
        createdAt: record.createdAt,
      }),
    });
  }
};

const upgradeIdentityUnchanged = (
  entry: UserTranscriptEntry | AssistantTranscriptEntry,
  record: SnapshotRecord
): boolean => {
  if (record.turnId !== undefined && entry.turnId !== record.turnId) return false;
  if (record.iteration !== undefined && entry.iteration !== record.iteration)
    return false;
  if (record.createdAt !== undefined && entry.createdAt !== record.createdAt)
    return false;
  if (entry.kind === 'assistant') {
    if (record.artifactId !== undefined && entry.artifactId !== record.artifactId) {
      return false;
    }
    if (record.messageIndex !== undefined && entry.messageIndex !== record.messageIndex) {
      return false;
    }
  }
  return true;
};

/** The payload discriminator classifies the artifact; its message never does. */
const artifactRecordKind = (
  payloadType: string | undefined
): SnapshotRecordKind | undefined => {
  switch (payloadType) {
    case 'user_message':
      return 'user';
    case 'assistant_message':
      return 'assistant';
    case 'compaction_marker':
    case 'turn_termination':
      return 'marker';
    default:
      return undefined;
  }
};

const artifactMarker = (
  payload: RunArtifactPayload | undefined
): TranscriptMarker | undefined => {
  if (payload?.type === 'compaction_marker') return { marker: 'compaction_marker' };
  if (
    payload?.disposition === 'cancelled' ||
    (!payload?.disposition && isCancelMarker(payload?.message))
  ) {
    return { marker: 'turn_cancelled' };
  }
  if (
    payload?.disposition === 'failed' ||
    (!payload?.disposition && isFailedTurnMarker(payload?.message))
  ) {
    return { marker: 'turn_failed', reason: payload?.reason ?? null };
  }
  return undefined;
};

const messageRecordKind = (
  message: Message
): { kind: SnapshotRecordKind; marker?: TranscriptMarker } => {
  if (isCancelMarker(message)) {
    return { kind: 'marker', marker: { marker: 'turn_cancelled' } };
  }
  if (isFailedTurnMarker(message)) {
    return { kind: 'marker', marker: { marker: 'turn_failed', reason: null } };
  }
  return { kind: message.role === 'user' ? 'user' : 'assistant' };
};

const isFailedTurnMarker = (message: Message | undefined): boolean =>
  message?.event_type === 'agent.turn_failed' && !!message.system_generated;

// ---- seam #1: what the fallback may claim ----------------------------------

interface ClaimIndex {
  claimAssistant: (signature: string) => string | undefined;
}

/**
 * The twin-match ledger for ONE fetch: claimable entries are streamed, settled,
 * signature-matched and past the last persisted row. A still-streaming entry is
 * never claimable (a live draft that coincidentally matches is a different
 * message), and each candidate is consumed once, so a reply repeated across
 * turns pairs up in order instead of collapsing into one row.
 */
const createClaimIndex = (draft: TranscriptDraft): ClaimIndex => {
  let lastPersistedPosition = -1;
  draft.order.forEach((entryId, index) => {
    if (draft.byId[entryId]?.source === 'persisted') lastPersistedPosition = index;
  });

  const candidates = draft.order.slice(lastPersistedPosition + 1).filter((entryId) => {
    const entry = draft.byId[entryId];
    return entry?.kind === 'assistant' && entry.source === 'streamed' && !entry.streaming;
  });

  return {
    claimAssistant: (signature) => {
      const index = candidates.findIndex((entryId) => {
        const entry = draft.byId[entryId];
        return (
          entry?.kind === 'assistant' &&
          entry.source === 'streamed' &&
          assistantMessageSignature(entry.message) === signature
        );
      });
      if (index < 0) return undefined;
      return candidates.splice(index, 1)[0];
    },
  };
};

// ---- seam #2: messages[] as a tool-result co-source -------------------------

const ingestToolResults = (
  draft: TranscriptDraft,
  messages: readonly Message[]
): void => {
  for (const message of messages) {
    for (const block of message.blocks) {
      if (block.kind !== 'tool_result') continue;
      const entry = ensureToolEntry(draft, block.tool_call_id, 'persisted');
      if (entry.result === block && entry.source === 'persisted') continue;
      writeEntry(draft, { ...entry, source: 'persisted', result: block });
    }
  }
};

const ingestApprovals = (
  draft: TranscriptDraft,
  approvals: readonly ApprovalResponse[]
): void => {
  for (const approval of approvals) {
    const entry = ensureToolEntry(draft, approval.tool_call_id, 'persisted');
    // The snapshot is the verdict even if `approval_resolved` was missed.
    const retire =
      approval.status !== 'pending' &&
      !!entry.pendingApproval &&
      !entry.pendingApprovalStale;
    if (entry.approval === approval && !retire) continue;
    writeEntry(draft, {
      ...entry,
      approval,
      ...(retire && { pendingApprovalStale: true }),
    });
  }
};
